import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/auth/auth.service';
import { DashboardSettings } from './models/dashboard-settings.model';
import { DashboardWidget } from './models/dashboard-widget.model';
import { WidgetConfiguration, WidgetType } from './models/widget-configuration.model';

@Injectable({
  providedIn: 'root',
})
export class WidgetService {
  private dashboardSettingsEndpoint: string = '/api/DashboardSettings';
  private dashboardSettingId: number = 0;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  createDashboardSettings(dashboardSettings: WidgetConfiguration[]): Observable<DashboardSettings> {
    const body: DashboardSettings = {
      id: this.dashboardSettingId,
      userName: this.authService.getUserMail(),
      widgets: dashboardSettings.map(this.mapWidgetToCreateApi),
    };

    const baseUrl: string = `${environment.apiUrl}${this.dashboardSettingsEndpoint}`;

    if (this.dashboardSettingId) {
      return this.http.put<DashboardSettings>(`${baseUrl}/${this.dashboardSettingId}`, {
        id: this.dashboardSettingId,
        dashboardSetting: body,
      } as {
        id: number;
        dashboardSetting: DashboardSettings;
      });
    }

    return this.http.post<DashboardSettings>(baseUrl, body).pipe(
      tap(({ id }) => {
        this.dashboardSettingId = id ?? 0;
      })
    );
  }

  getDashboardSettings(): Observable<WidgetConfiguration[]> {
    const url: string = `${environment.apiUrl}${this.dashboardSettingsEndpoint}`;

    return this.http.get<DashboardSettings>(url).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.dashboardSettingId = 0;
          return of({
            id: 0,
            userName: this.authService.getUserMail(),
            widgets: [],
          } satisfies DashboardSettings);
        }
        throw error;
      }),
      tap(({ id }) => {
        this.dashboardSettingId = id ?? 0;
      }),
      map(({ widgets = [] }) => widgets.map(this.mapWidgetFromApi))
    );
  }

  private mapWidgetToCreateApi(widget: WidgetConfiguration): DashboardWidget {
    const id: number = widget.id ?? 0;

    return {
      id,
      type: widget.type,
      positionConfiguration: {
        id,
        cols: widget.positionConfiguration.cols ?? 0,
        rows: widget.positionConfiguration.rows ?? 0,
        minItemCols: widget.positionConfiguration.minItemCols ?? 0,
        minItemRows: widget.positionConfiguration.minItemRows ?? 0,
        x: widget.positionConfiguration.x ?? 0,
        y: widget.positionConfiguration.y ?? 0,
      },
      configurationJson: JSON.stringify(widget.configuration ?? {}),
    };
  }

  private mapWidgetFromApi(widget: DashboardWidget): WidgetConfiguration {
    return {
      id: widget.id,
      type: widget.type as WidgetType,
      positionConfiguration: {
        id: widget.positionConfiguration?.id ?? widget.id ?? 0,
        cols: widget.positionConfiguration?.cols ?? 1,
        rows: widget.positionConfiguration?.rows ?? 1,
        minItemCols: widget.positionConfiguration?.minItemCols ?? 1,
        minItemRows: widget.positionConfiguration?.minItemRows ?? 1,
        x: widget.positionConfiguration?.x ?? 0,
        y: widget.positionConfiguration?.y ?? 0,
      },
      configuration: JSON.parse(widget.configurationJson),
    };
  }
}
